import { Component, OnInit } from '@angular/core';
import { Papa } from 'ngx-papaparse';
import { saveAs } from 'file-saver';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/auth/service/auth.service';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { from, map, mergeMap } from 'rxjs';
import { FindCompanyRequest, SearchService } from 'ldt-people-api';

interface Col {
  [key: string]: string;
}

@Component({
    selector: 'app-find-companies',
    templateUrl: './find-companies.component.html',
    styleUrls: ['./find-companies.component.scss'],
    standalone: false
})
export class FindCompaniesComponent {
  orgId: string;
  isDragOver: boolean = false;
  uploadError: string | undefined = undefined;
  uploadMessage: string | undefined = undefined;
  uploadPercent: number | undefined = undefined;
  downloadFormat: 'csv' | 'jsonl' = 'csv';

  constructor(
    private searchService: SearchService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private notify: NotificationService,
    private router: Router,
    private papa: Papa
  ) {}

  ngOnInit(): void {
    let orgId = this.route.parent?.snapshot.paramMap.get('orgId');
    if (!orgId) {
      orgId = this.authService.getSelectedOrgIdValue;

      if (!orgId) {
        this.notify.error('Invalid path');
        this.router.navigateByUrl('/main');
      }
    }

    this.orgId = orgId;
  }

  dragOver(evt: DragEvent) {
    if (evt.dataTransfer) evt.dataTransfer.dropEffect = 'copy';
    evt.preventDefault();
  }

  dropFiles(evt: DragEvent) {
    if (!evt.dataTransfer) return;

    evt.preventDefault();
    this.uploadFiles(evt.dataTransfer.files);
  }

  async uploadFiles(files: FileList) {
    this.uploadError = undefined;

    if (files.length !== 1) {
      this.uploadError = 'Only one file may be uploaded at a time.';
      throw new Error(`expected exactly one file, got ${files.length}`);
    }

    if (!files[0].name.endsWith('.csv')) {
      this.uploadError = 'Only CSV files may be uploaded. Please try again.';
      throw new Error(`File must be a CSV ${files[0].name}`);
    }

    const originalFileName = files[0].name.split('.').slice(0, -1).join('.');
    const date = new Date();
    const dateTime =
      date.getFullYear().toString() +
      (date.getMonth() + 1).toString().padStart(2, '0') +
      date.getDate().toString().padStart(2, '0') +
      date.getHours().toString().padStart(2, '0') +
      date.getMinutes().toString().padStart(2, '0') +
      date.getSeconds().toString().padStart(2, '0');
    const newFileName = `${originalFileName}_livedata_find_${dateTime}`;

    this.uploadPercent = 1;

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target) {
        const contents = e.target.result as string;

        const guessFieldName = (header: string): string => {
          header = header.toLowerCase();
          if (header.includes('name')) return 'name';
          if (header.includes('linkedin')) return 'linkedin';
          if (header.includes('ticker')) return 'ticker';
          if (header.includes('domain') || header.includes('site')) return 'domain';
          if (header.includes('company')) return 'name';
          return '';
        };

        const results = this.papa.parse(contents, {
          header: true,
          transformHeader: guessFieldName,
          skipEmptyLines: true,
        });

        // Create a list of processed rows, pairing original CSV data with generated search fields
        const processedRows: {
          originalRow: Col;
          findRequest: FindCompanyRequest;
        }[] = [];
        const parsedCsvData: Col[] = results.data as Col[]; // Type assertion for clarity

        const validSearchFields: string[] = ['name', 'domain', 'ticker', 'linkedin'];

        parsedCsvData.forEach((col: Col) => {
          Object.entries(col).forEach(([fieldName, searchTerm]) => {
            if (!searchTerm) return;
            // Skip columns whose header didn't map to a recognized search field
            // (guessFieldName returns '' for those). Sending a blank search_field
            // causes the API to reject the request with a 400.
            if (!validSearchFields.includes(fieldName)) return;

            processedRows.push({
              originalRow: col,
              findRequest: {
                search_field: fieldName as FindCompanyRequest.SearchFieldEnum,
                search_value: searchTerm,
              },
            });
          });
        });

        // Update results.data to contain only the original rows that are valid for searching
        results.data = processedRows.map((pr) => pr.originalRow);

        let completedCalls = 0;
        let totalFound = 0;
        let allFound: any[] = [];

        from(processedRows)
          .pipe(
            mergeMap(
              (row, index) =>
                this.searchService.findCompany(this.orgId, row.findRequest).pipe(
                  map((response) => ({
                    response,
                    search_field: row.findRequest.search_field,
                    index: index,
                  }))
                ),
              10
            )
          )
          .subscribe(({ response, search_field, index }) => {
            results.data[index]['ldt_found'] = response.count! > 0; // TODO : fix the schema
            results.data[index]['ldt_search_field'] = search_field;
            results.data[index]['ldt_count'] = response.count;
            if (response.companies && response.companies.length > 0) {
              results.data[index]['ldt_company_name'] = response.companies[0].name;
              results.data[index]['ldt_company_id'] = response.companies[0].id;
              results.data[index]['ldt_company_domain'] = (response.companies[0] as any).domain; // TODO fix the schema
              results.data[index]['ldt_company_ticker'] = response.companies[0].ticker;
              results.data[index]['ldt_company_linkedin'] = response.companies[0].linkedin;
            }

            completedCalls++;
            this.uploadPercent = (completedCalls / processedRows.length) * 100;

            // If all calls are completed, generate files
            if (completedCalls === processedRows.length) {
              this.uploadPercent = undefined;

              if (this.downloadFormat === 'csv') {
                const csv = this.papa.unparse(results.data);
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                saveAs(blob, `${newFileName}.csv`);
              }

              this.uploadMessage =
                'Find complete and file downloaded to your PC (' +
                newFileName +
                '). ' +
                totalFound +
                ' found.';
            }
          });
      }
    };
    reader.readAsText(files[0]);
  }
}
